import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Building2, Check, Clock, Globe, KeyRound, Loader2, Rocket, Store, Zap } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { PageHead, Pill, TONES, inputCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";

const TONE = TONES.settings;
const ICONS = { kiosk: Store, storefront: Building2, department: Rocket, standalone: Globe };
const CYCLES = [
    { key: "monthly", label: "Monthly" },
    { key: "yearly", label: "Yearly · save up to 45%" },
    { key: "lifetime", label: "Lifetime" },
];

const priceFor = (tier, cycle) =>
    cycle === "lifetime" ? tier.price_lifetime : cycle === "yearly" ? tier.price_year : tier.price_month;
const keyFor = (tier, cycle) =>
    cycle === "lifetime" ? tier.lifetime_key : cycle === "yearly" ? tier.yearly_key : tier.monthly_key;

const dateOf = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

function specs(t) {
    return [
        ["Live ads", t.listing_cap ? `${t.listing_cap} at a time` : "Unlimited"],
        ["Your products in the directory catalogs", t.catalog ? "Yes" : "No"],
        ["The live designers", t.designers ? "All four" : "Not included"],
        ["Store page", t.store_page === "full" ? "Full page" : t.store_page === "mini" ? "Starter profile" : "—"],
        ["Featured slots", t.featured_slots || "0"],
        ["Opens", t.needs_review ? "After 13thav support reviews you" : "Right away"],
    ];
}

export default function OpenSpace() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const wantUnit = params.get("unit") || "";
    const wantTier = params.get("tier") || "";
    const wantCycle = params.get("cycle") || "";
    const [data, setData] = useState(null);
    const [me, setMe] = useState(null);
    const [cycle, setCycle] = useState(["monthly", "yearly", "lifetime"].includes(wantCycle) ? wantCycle : "monthly");
    const [picked, setPicked] = useState(null);
    const [busy, setBusy] = useState(false);
    const [cityWarning, setCityWarning] = useState(null);
    const [form, setForm] = useState({ name: "", city: "", what_you_sell: "", pickup_address: "", ships: false, phone: "" });

    useEffect(() => { api.get("/stores/tiers").then(({ data }) => setData(data)).catch(() => { }); }, []);
    useEffect(() => {
        if (!user) { setMe(null); return; }
        api.get("/stores/me").then(({ data }) => {
            setMe(data);
            if (data.store) setForm((f) => ({ ...f, ...data.store }));
        }).catch(() => { });
    }, [user]);

    useEffect(() => {
        if (picked?.id !== "department" || !form.city?.trim()) { setCityWarning(null); return; }
        const t = setTimeout(() => {
            api.get("/stores/city-check", { params: { city: form.city, tier: "department" } })
                .then(({ data }) => setCityWarning(data.warning)).catch(() => { });
        }, 500);
        return () => clearTimeout(t);
    }, [picked, form.city]);

    useEffect(() => {
        if (!wantTier || !data) return;
        const el = document.querySelector(`[data-testid="space-card-${wantTier}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [wantTier, data]);

    /** Came off the pitch page with a plan already picked — open the form for them. */
    useEffect(() => {
        if (!wantTier || !data || !me || me.store) return;
        const t = (data.tiers || []).find((x) => x.id === wantTier);
        if (t) setPicked((p) => p || t);
    }, [wantTier, data, me]);

    const claimUnit = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/stores/me/unit", { unit: wantUnit });
            toast.success(data.note);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const pay = async (tier) => {
        const lookup = keyFor(tier, cycle);
        if (!lookup) { toast.error("That billing option isn't available on this space"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
    };

    const openSpace = (tier) => { if (!me?.store) { setPicked(tier); return; } pay(tier); };

    const submitApplication = async (e) => {
        e?.preventDefault();
        if (!form.name.trim() || !form.city.trim()) { toast.error("Shop name and city required"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/stores/apply", { ...form, tier: picked.id, unit: wantUnit || undefined });
            toast.success(data.note);
            if (data.warning) toast.warning(data.warning, { duration: 9000 });
            const fresh = await api.get("/stores/me");
            setMe(fresh.data);
            if (picked.needs_review) {
                setPicked(null); setBusy(false);
                navigate("/account?setup=1");
                return;
            }
            pay(picked);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
    };

    const currentTier = me?.tier?.id;
    const store = me?.store;
    const vendor = me?.vendor || null;

    return (
        <BackOffice>
            <PageHead kicker="Shop settings" tone={TONE} title="Space & plan" testid="open-space-head"
                sub="Your plan, what it costs and what it opens up. You can change it any time. Your first month is 50% off and you can cancel any time."
                right={
                    <div className="flex flex-wrap justify-end gap-1.5" data-testid="cycle-tabs">
                        {CYCLES.map((c) => (
                            <Pill key={c.key} active={cycle === c.key} testid={`cycle-${c.key}`}
                                onClick={() => setCycle(c.key)}>{c.label}</Pill>
                        ))}
                    </div>
                } />

            {wantUnit && (
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-500/40 bg-amber-50 px-4 py-2.5 text-sm text-zinc-900"
                    data-testid="held-unit">
                    <KeyRound size={14} className="text-amber-700" />
                    <span className="font-semibold">Unit {wantUnit} is held for you</span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-700">
                        off the shop directory · nothing charged yet
                    </span>
                    {store ? (
                        <button onClick={claimUnit} disabled={busy} data-testid="claim-unit"
                            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-50 hover:bg-black disabled:opacity-50">
                            {busy ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />} take unit {wantUnit}
                        </button>
                    ) : (
                        <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-700">
                            pick the space below and it's yours
                        </span>
                    )}
                </div>
            )}

            {store && (
                <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-black/10 bg-zinc-100 px-4 py-2.5 text-sm text-zinc-900"
                    data-testid="my-space">
                    {store.status === "pending_review" ? <Clock size={14} /> : <BadgeCheck size={14} />}
                    <span className="font-semibold">You rent a {me.tier.name} — {store.name}</span>
                    {vendor?.comped ? (
                        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-700" data-testid="plan-comped">free for life · on the house</span>
                    ) : vendor?.lifetime ? (
                        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-700">paid once · yours for good</span>
                    ) : vendor?.price_month ? (
                        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-700">
                            ${vendor.interval === "yearly" ? `${vendor.price_year}/yr` : `${vendor.price_month}/mo`}
                            {vendor.trial_ends_at ? ` · trial ends ${dateOf(vendor.trial_ends_at)}` : ""}
                        </span>
                    ) : null}
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-700">
                        · {store.status.replace("_", " ")}
                    </span>
                    <Link to="/account/store" data-testid="edit-store-cta"
                        className="ml-auto font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-900 hover:underline">
                        edit my store →
                    </Link>
                </p>
            )}

            <div className="mt-5 space-y-4" data-testid="space-grid">
                {(data?.tiers || []).map((t) => {
                    const Icon = ICONS[t.id] || Store;
                    const price = priceFor(t, cycle);
                    const mine = currentTier === t.id;
                    return (
                        <div key={t.id} id={t.id === "standalone" ? "virtual-brick-and-mortar" : undefined}
                            data-testid={`space-card-${t.id}`}
                            className={`scroll-mt-32 overflow-hidden rounded-lg border bg-white ${mine ? "border-zinc-900 ring-1 ring-zinc-900" : wantTier === t.id ? "border-amber-500 ring-1 ring-amber-500" : "border-black/10"}`}>
                            <header className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-black/10 px-4 py-3 ${mine ? "bg-zinc-800" : "bg-zinc-100"}`}>
                                <Icon size={16} className={mine ? "text-zinc-50" : "text-zinc-800"} />
                                <h2 className={`font-display text-lg font-black tracking-tight ${mine ? "text-zinc-50" : "text-zinc-900"}`}>
                                    {t.name}
                                </h2>
                                <span className={`font-mono text-[11px] font-bold uppercase tracking-[0.14em] ${mine ? "text-zinc-300" : "text-zinc-700"}`}>
                                    {price == null ? "no lifetime option" : `$${price.toLocaleString()}${cycle === "lifetime" ? " once" : cycle === "yearly" ? " / yr" : " / mo"}`}
                                </span>
                                {t.outside_mall && (
                                    <span className="rounded border border-amber-500/50 bg-amber-50 px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-amber-700">
                                        your own private site
                                    </span>
                                )}
                                {mine && (
                                    <span className="ml-auto rounded bg-zinc-50 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-900"
                                        data-testid={`space-mine-${t.id}`}>your space</span>
                                )}
                            </header>

                            <div className="p-4 sm:p-5">
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-black/10 pb-4" data-testid={`space-price-${t.id}`}>
                                    <p className="font-display text-3xl font-black tracking-tight text-zinc-900">
                                        {price == null ? "—" : `$${price.toLocaleString()}`}
                                        {price != null && (
                                            <span className="text-sm font-bold text-zinc-600">
                                                {cycle === "lifetime" ? " once" : cycle === "yearly" ? " / yr" : " / mo"}
                                            </span>
                                        )}
                                    </p>
                                    <p className="max-w-xs text-xs text-zinc-700">
                                        {price == null ? "Lifetime is Pro Shop only."
                                            : cycle === "monthly" && t.price_year
                                                ? `Or $${t.price_year.toLocaleString()} a year — save $${(t.price_month * 12 - t.price_year).toLocaleString()}.`
                                                : cycle === "yearly" ? `Instead of $${(t.price_month * 12).toLocaleString()} paid monthly.`
                                                    : "One payment, no renewal."}
                                    </p>
                                    <div className="ml-auto">
                                        {!user ? (
                                            <Link to="/login" className="block">
                                                <GreenButton data-testid={`space-login-${t.id}`}>Log in to open</GreenButton>
                                            </Link>
                                        ) : mine ? (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900"
                                                    data-testid={`space-current-${t.id}`}>this is what you rent</p>
                                                <Link to="/account/store" data-testid={`space-manage-${t.id}`}
                                                    className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800 hover:border-zinc-900">
                                                    manage my store
                                                </Link>
                                            </div>
                                        ) : (
                                            <GreenButton onClick={() => openSpace(t)}
                                                disabled={busy || (cycle === "lifetime" && !t.lifetime_key)}
                                                data-testid={`space-open-${t.id}`}>
                                                {busy ? <Loader2 className="animate-spin" size={16} />
                                                    : currentTier && currentTier !== "customer" ? `Move to ${t.name}` : `Open a ${t.name}`}
                                            </GreenButton>
                                        )}
                                    </div>
                                </div>
                                <p className="mt-2 text-[11px] text-zinc-600">
                                    50% off your first month · cancel any time · 13thav keeps {data?.fee_pct ?? 1}% of card sales in the directory and 0% on a private site
                                </p>

                                <p className="mt-4 text-sm font-semibold text-zinc-900">{t.tagline}</p>
                                <ul className="mt-3 space-y-1.5">
                                    {t.perks.map((p) => (
                                        <li key={p} className="flex gap-2 text-sm text-zinc-900">
                                            <Check size={15} className="mt-0.5 shrink-0 text-zinc-500" /> {p}
                                        </li>
                                    ))}
                                </ul>
                                <dl className="mt-4 divide-y divide-black/10 border-t border-black/10">
                                    {specs(t).map(([k, v]) => (
                                        <div key={k} className="flex items-baseline gap-2 py-1.5">
                                            <dt className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600">{k}</dt>
                                            <dd className="ml-auto text-xs font-semibold text-zinc-900">{v}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-black/10 bg-zinc-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-zinc-900">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900">
                        <Zap size={13} /> Featured boost
                    </span>
                    <span className="ml-2">Any space can push one ad to the top of its tab for 7 days — ${data?.boost?.price ?? "1.98"}.</span>
                </p>
                <Link to="/account/listings" data-testid="boost-link"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800 hover:border-zinc-900">
                    Boost an ad
                </Link>
            </div>

            <p className="mt-4 text-sm text-zinc-700">
                Looking for buyer discounts instead? That's <Link to="/wholesale" className="font-semibold text-zinc-900 underline">Crew Perks</Link> —
                a membership that saves you money when you buy. Spaces are for selling.
            </p>

            {picked && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="apply-modal">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setPicked(null)} />
                    <form onSubmit={submitApplication} className="relative w-full max-w-lg rounded-lg border border-black/10 bg-white p-6 shadow-2xl">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: TONE }}>
                            Open a {picked.name}
                        </p>
                        <h3 className="mt-1.5 font-display text-2xl font-black tracking-tight text-zinc-900">Tell us about your shop.</h3>
                        <div className="mt-5 space-y-3">
                            <input className={inputCls} placeholder="Shop name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" />
                            <input className={inputCls} placeholder="City, State" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="apply-city" />
                            <input className={inputCls} placeholder="What do you sell?" value={form.what_you_sell} onChange={(e) => setForm({ ...form, what_you_sell: e.target.value })} data-testid="apply-sells" />
                            <input className={inputCls} placeholder="Pickup or ship-from address" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} data-testid="apply-address" />
                            <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-phone" />
                            <label className="flex items-center gap-2 text-sm text-zinc-900">
                                <input type="checkbox" checked={form.ships} onChange={(e) => setForm({ ...form, ships: e.target.checked })} className="h-4 w-4 accent-zinc-900" data-testid="apply-ships" />
                                I ship orders as well as local pickup
                            </label>
                        </div>
                        {cityWarning && (
                            <div className="mt-4 rounded-lg border border-amber-400 bg-amber-50 p-4" data-testid="apply-city-warning">
                                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">Turf check</p>
                                <p className="mt-1.5 text-xs text-zinc-800">{cityWarning}</p>
                            </div>
                        )}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <GreenButton type="submit" disabled={busy} data-testid="apply-submit">
                                {busy ? <Loader2 className="animate-spin" size={16} /> : picked.needs_review ? "Submit application" : "Continue to payment"}
                            </GreenButton>
                            <button type="button" onClick={() => setPicked(null)} data-testid="apply-cancel"
                                className="font-mono text-[11px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900">cancel</button>
                        </div>
                        <p className="mt-3 text-[11px] text-zinc-600">
                            {picked.needs_review
                                ? "Pro Shop applications get reviewed before the designers unlock. No charge until we approve you."
                                : "The next step is Stripe. Your first month is 50% off and you can cancel any time."}
                        </p>
                    </form>
                </div>
            )}
        </BackOffice>
    );
}
